/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Camera, User, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorServices, useVendorPortfolioImages } from '../../services/vendorService';
import { useCheckFavorite, useAddFavorite, useRemoveFavorite } from '../../services/favoriteVendorService';
import { useToast, ToastContainer } from '../../components/shared/Toast';
import useShare from '../../hooks/useShare';
import VendorHeroGallery from '../../components/client/VendorHeroGallery';
import VendorHeaderInfo from '../../components/client/VendorHeaderInfo';
import VendorKeyDetails from '../../components/client/VendorKeyDetails';
import VendorHighlights from '../../components/client/VendorHighlights';
import VendorAmenities from '../../components/client/VendorAmenities';
import VendorBookingWidget from '../../components/client/VendorBookingWidget';
import VendorSocialMedia from '../../components/client/VendorSocialMedia';
import VendorReviews from '../../components/client/VendorReviews';
import ServiceCard from '../../components/client/ServiceCard';
import VendorProfileSkeleton from '../../components/shared/VendorProfileSkeleton';

const ClientVendorProfile = () => {
  const { id } = useParams();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [showMobileBooking, setShowMobileBooking] = useState(false);
  
  // Fetch vendor services data from API
  const { data: servicesResponse, isLoading, error } = useVendorServices(id);
  
  // Transform API data to get vendor ID
  const services = servicesResponse?.data || [];
  const vendor = services[0]?.vendor_profile;
  const vendorId = vendor?.id || id;
  
  // Check if vendor is favorite (only when authenticated and vendorId is available)
  const { data: favoriteData, isLoading: checkingFavorite } = useCheckFavorite(vendorId, {
    enabled: !!vendorId && isAuthenticated
  });
  const isFavorited = favoriteData?.is_favorite || false;
  const favoriteId = favoriteData?.favorite_id || null;
  
  // Mutations for adding/removing favorites
  const addFavoriteMutation = useAddFavorite({
    onSuccess: (data) => {
      // Optimistically update the favorite status immediately
      // The query will refetch automatically, but we can update UI immediately
      toast.success('Added to Favorites', 'Vendor has been added to your favorites!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to add to favorites';
      toast.error('Error', errorMessage);
    }
  });
  
  const removeFavoriteMutation = useRemoveFavorite({
    onSuccess: () => {
      // Optimistically update the favorite status immediately
      toast.success('Removed from Favorites', 'Vendor has been removed from your favorites.');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove from favorites';
      toast.error('Error', errorMessage);
    }
  });
  
  // Handle favorite toggle
  const handleToggleFavorite = () => {
    if (!isAuthenticated) {
      return;
    }
    
    if (isFavorited) {
      // Always try to remove, even if favoriteId is 'optimistic' - the mutation will handle it
      removeFavoriteMutation.mutate({ favoriteId, vendorId });
    } else if (vendorId) {
      addFavoriteMutation.mutate({ vendorId: vendorId });
    }
  };
  
  const isFavoriteLoading = addFavoriteMutation.isPending || removeFavoriteMutation.isPending || checkingFavorite;
  
  // Fetch vendor portfolio images
  const { data: portfolioImagesResponse, isLoading: portfolioLoading } = useVendorPortfolioImages(id);

  // Transform API data (vendor already defined above)
  const vendorUser = vendor?.user;
  const portfolioImages = portfolioImagesResponse?.data || [];
  
  // Get unique categories
  const categories = [...new Map(
    services
      .map(service => service.service_category)
      .filter(Boolean)
      .map(cat => [cat.id, cat])
  ).values()];
  
  // Build portfolio image gallery - only portfolio images (image_type='portfolio')
  const portfolioGalleryImages = [];
  
  // Add portfolio images only (from user_images with image_type='portfolio')
  portfolioImages.forEach(image => {
    if (image.image_url) {
      portfolioGalleryImages.push(image.image_url);
    }
  });

  // Calculate minimum price
  const minPrice = services.length > 0 
    ? Math.min(...services.map(s => parseFloat(s.base_price) || Infinity).filter(p => p > 0 && p !== Infinity))
    : null;

  // Share functionality hook
  const { handleShare } = useShare({
    title: vendor?.business_name || 'Vendor',
    text: `Check out ${vendor?.business_name} on PhotoBooth`,
  });

  if (isLoading || portfolioLoading) {
    return <VendorProfileSkeleton />;
  }

  if (error || !services.length) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 border-2 border-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Camera className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-light text-black mb-3">Vendor Not Found</h2>
          <p className="text-gray-600 mb-8 font-light">
            The vendor you're looking for doesn't exist or has no services available.
          </p>
          <Link 
            to="/browse" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer 
        toasts={toast.toasts} 
        onRemove={toast.removeToast}
        position="top-right"
      />
      <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Back Button - Mobile Only */}
        <div className="mb-3 sm:mb-4 md:mb-6 lg:hidden">
          <Link 
            to="/browse" 
            className="inline-flex items-center gap-2 text-gray-600 active:text-black transition-colors font-light touch-manipulation"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
                  </div>

        {/* Header Info - At Top */}
        <div className="mb-4 sm:mb-6">
          <VendorHeaderInfo
            vendor={vendor}
            isFavorited={isFavorited}
            onToggleFavorite={handleToggleFavorite}
            onShare={handleShare}
            isLoading={isFavoriteLoading}
            isAuthenticated={isAuthenticated}
          />
              </div>

        {/* Hero Gallery - Portfolio Images Only */}
        <div className="mb-4 sm:mb-6 md:mb-8 -mx-3 sm:mx-0">
          <VendorHeroGallery 
            images={portfolioGalleryImages.filter(Boolean)} 
            vendorName={vendor?.business_name}
            onShowAll={() => setShowAllPhotos(true)}
          />
          </div>
                    
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 lg:gap-12">
          {/* Left Column - Main Content */}
          <div className="space-y-8">

            {/* Key Details Bar - Rating and Location */}
            <VendorKeyDetails vendor={vendor} servicesCount={services.length} />

            {/* Vendor Host Info */}
            {vendorUser && (
              <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                  {vendorUser.profile_image_url ? (
                    <img
                      src={vendorUser.profile_image_url}
                      alt={vendorUser.name || 'Vendor'}
                      className="w-full h-full rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-base sm:text-lg font-medium text-black">
                     by {vendorUser.name || vendor?.business_name}
                  </p>
                  {vendor?.years_experience && (
                    <p className="text-sm sm:text-base text-gray-600 font-light">
                      {vendor.is_verified ? 'Verified vendor' : 'Vendor'} · {vendor.years_experience}+ years hosting
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {vendor?.description && (
              <div className="pb-6 border-b border-gray-200">
                <p className="text-sm sm:text-base text-gray-700 font-light leading-relaxed whitespace-pre-line">
                  {vendor.description}
                </p>
              </div>
            )}

            {/* Social Media */}
            {vendor?.social_media && (
              <div className="pb-6 border-b border-gray-200">
                <VendorSocialMedia socialMedia={vendor.social_media} />
              </div>
            )}

            {/* Highlights */}
            <div className="pb-6 border-b border-gray-200">
              <VendorHighlights vendor={vendor} services={services} />
            </div>

            {/* What this place offers */}
            <div className="pb-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-light text-black mb-6">
                What this vendor offers
              </h2>
              <VendorAmenities services={services} categories={categories} />
            </div>

            {/* Services Section */}
            <div>
              <h2 className="text-lg sm:text-xl font-light text-black mb-6">
                Services
              </h2>

              {services.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                  {services.map((service) => {
                    const category = service.service_category;
                    return (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        vendorRating={vendor?.average_rating}
                        category={category}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 border border-gray-200 rounded-lg">
                  <Camera className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-light text-black mb-2">No services found</h3>
                  <p className="text-gray-500 font-light">No services available at the moment.</p>
                </div>
              )}
            </div>

            {/* Reviews Section */}
            <div className="pb-6 border-b border-gray-200">
              <VendorReviews vendorId={id} />
            </div>
          </div>

          {/* Right Column - Sticky Booking Widget (Desktop Only) */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <VendorBookingWidget
                vendor={vendor}
                services={services}
                minPrice={minPrice}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Booking Button - Fixed Bottom */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-light text-black">
                  ${minPrice ? minPrice.toFixed(2) : 'Contact'}
                </span>
                {services.length > 0 && minPrice && (
                  <span className="text-sm font-light text-gray-600">
                    starting price
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowMobileBooking(true)}
              className="px-6 py-2 bg-black text-white rounded-lg text-sm font-light hover:bg-gray-800 transition-colors"
            >
              View Services
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Booking Modal */}
      {showMobileBooking && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileBooking(false)}
          />
          
          {/* Modal Content */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-lg font-light text-black">Booking Details</h3>
              <button
                onClick={() => setShowMobileBooking(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="p-4">
              <VendorBookingWidget
                vendor={vendor}
                services={services}
                minPrice={minPrice}
              />
            </div>
          </div>
        </div>
      )}

      {/* Full Photo Gallery Modal */}
      {showAllPhotos && (
        <div 
          className="fixed inset-0 bg-black z-[99999] overflow-y-auto"
          onClick={() => setShowAllPhotos(false)}
        >
          <div className="max-w-7xl mx-auto p-4 sm:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light text-white">
                {portfolioGalleryImages.length} photos
              </h2>
              <button
                onClick={() => setShowAllPhotos(false)}
                className="text-white hover:opacity-70 transition-opacity"
              >
                <span className="text-2xl">×</span>
              </button>
                            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {portfolioGalleryImages.map((image, index) => (
                <div key={index} className="relative aspect-video overflow-hidden rounded-lg bg-gray-200 animate-pulse">
                  <img
                    src={image}
                    alt={`${vendor?.business_name} - Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onLoad={(e) => {
                      e.target.parentElement.classList.remove('animate-pulse', 'bg-gray-200');
                    }}
                    onError={(e) => {
                      e.target.parentElement.classList.remove('animate-pulse');
                      e.target.parentElement.classList.add('bg-gray-100');
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ClientVendorProfile;
